/**
 * 쇼핑 정책 설정 (고도몰 ID51_3 "장바구니 설정" + "관심상품 설정" + "간편구매 설정").
 *
 * 한 화면에 2섹션 — prefix 가 서로 다른 두 서비스의 싱글톤 설정을 합친다:
 *   - 장바구니: order-service  GET/PUT `/admin/v1/orders/cart-policy-settings`
 *               (게이트웨이 `/admin/v1/orders/**` RewritePath → BE `/api/admin/v1/orders/**`)
 *   - 관심상품/간편구매: user-service GET/PUT `/admin/v1/wishlist/policy-settings`
 *               (게이트웨이 admin-catchall → user-service, rewrite 없음)
 *
 * 두 응답 모두 v2 envelope `{ success, data, error, meta }`. PUT 은 전체폼 제출 +
 * `Idempotency-Key`(재시도 안전). BE 가 enum 미상수 값을 400 으로 거부한다.
 */

/* ───────────────────────────── 장바구니 (order-service) ───────────────────────────── */

/** 담기 후 이동 동작 힌트(FE). BE 는 표시값만 제공하고 실제 이동은 storefront 가 수행한다. */
export type CartAddRedirect = 'TO_CART' | 'STAY' | 'CONTINUE_SHOPPING';
/** 동일 상품 재담기 정책. MERGE=기존 라인 수량 합산 / SEPARATE=별도 라인. */
export type CartDupPolicy = 'MERGE' | 'SEPARATE';
/** 품절 상품 처리. KEEP=조회 시 유지/표시 / AUTO_REMOVE=조회 응답에서 자동 제외(표시 레벨). */
export type CartSoldoutPolicy = 'KEEP' | 'AUTO_REMOVE';

/** 라인당 최대 담기 수량의 절대 상한 — BE 도메인이 1..50,000 으로 클램프(QA-605, order #129. 종전 999). */
export const CART_MAX_QTY_HARD_LIMIT = 50000;

/** 장바구니에 담을 수 있는 최대 상품(라인) 수의 하한 — BE 도메인이 Math.max(0, x) 클램프(상한 없음). */
export const CART_MAX_ITEMS_MIN = 0;

/** 장바구니 PUT 요청 본문 = 응답 본체에서 updatedBy/At 제외한 7필드. */
export interface CartPolicyPayload {
  /** 담기 후 이동 동작. */
  cartAddRedirect: CartAddRedirect;
  /** 비회원 장바구니 사용. */
  guestCartEnabled: boolean;
  /** 장바구니 보관기간(일). BE ≥1 클램프. */
  cartRetentionDays: number;
  /** 동일 상품 재담기. */
  cartDupPolicy: CartDupPolicy;
  /** 품절 상품 처리. */
  cartSoldoutPolicy: CartSoldoutPolicy;
  /** 라인당 최대 담기 수량. BE 1..999 클램프. */
  cartMaxQtyPerItem: number;
  /**
   * 장바구니에 담을 수 있는 최대 상품(라인) 수. cartMaxQtyPerItem(라인당 수량)과 별개 축.
   * 0 = 무제한. BE 클램프 = Math.max(0, x)(하한 0만, 상한 캡 없음 — 50,000 캡은 cartMaxQtyPerItem 전용).
   * (고도몰 §3 "개수 제한 20개", be-cart 확정 CartPolicySettings.update())
   */
  cartMaxItems: number;
}

/** 장바구니 GET/PUT 응답 = 7필드 + 감사 메타. */
export interface CartPolicySettings extends CartPolicyPayload {
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/** 장바구니 기본값(BE 미설정·로드 전 폼 시드 — BE defaults() / V36 seed 와 일치). */
export const DEFAULT_CART_POLICY: CartPolicySettings = {
  cartAddRedirect: 'TO_CART',
  guestCartEnabled: true,
  cartRetentionDays: 7,
  cartDupPolicy: 'MERGE',
  cartSoldoutPolicy: 'KEEP',
  cartMaxQtyPerItem: CART_MAX_QTY_HARD_LIMIT,
  cartMaxItems: 20,
  updatedBy: null,
  updatedAt: null,
};

/* ────────────────────── 관심상품/간편구매 (user-service) ────────────────────── */

/** 관심상품/간편구매 PUT 요청 본문 = 응답 본체에서 updatedBy/At 제외한 4필드. */
export interface WishlistPolicyPayload {
  /** 관심상품 기능 사용. false 면 BE 가 add 차단. */
  wishlistEnabled: boolean;
  /** 비회원(게스트) 관심상품 허용. 현재 wishlist 는 회원 전용이라 표시/후속 연동용. */
  guestWishlistEnabled: boolean;
  /** 회원 1인당 최대 관심상품 수. BE ≥1 클램프. */
  wishlistMaxItems: number;
  /** 간편구매(바로구매) 사용. storefront CTA 노출 게이트(표시값). */
  quickbuyEnabled: boolean;
}

/** 관심상품/간편구매 GET/PUT 응답 = 4필드 + 감사 메타. */
export interface WishlistPolicySettings extends WishlistPolicyPayload {
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/** 관심상품/간편구매 기본값(BE defaults() / V50 seed 와 일치). */
export const DEFAULT_WISHLIST_POLICY: WishlistPolicySettings = {
  wishlistEnabled: true,
  guestWishlistEnabled: false,
  wishlistMaxItems: 20,
  quickbuyEnabled: true,
  updatedBy: null,
  updatedAt: null,
};
